// @ts-nocheck
import React, { useState, useMemo, useEffect } from "react";
import {
  StyledModal,
  QueryBuilder,
  BaseQueryBuilderPayload,
  Tag,
  Tooltip,
  COLORS,
} from "@bikdotai/bik-component-library";
import { Help } from "@bikdotai/bik-component-library/icons";
import { SegmentValidator } from "../../app/action-block/filter/validator";
import { WarnIcon } from "../../icons/warnIcon";
import {
  extractHeaderAndSubHeader,
  sanitiseState,
  removeSubstring,
} from "../../app/action-block/filter/utils";
import { userPropertyFilterAPIHandler } from "../../app/action-block/filter/api";
import { CustomFieldHelper } from "../../app/action-block/filter/helpers/customFieldHelper";
import { clearSharedQueryBuilderCache } from "../../app/action-block/filter/helpers/queryBuilderCache";
import { useAppSelector } from "../../state/store";
import ChannelAccountDropdown, {
  useFlowChannelAccounts,
} from "./channelAccountDropdown";

const PROPERTY_PLACEHOLDERS = {
  HAS_CUSTOM_FIELD: "Custom date",
};

let builderState = {};
let filterTimer = false;
const segmentValidator = new SegmentValidator();
const customFieldHelper = new CustomFieldHelper();

const SpecialDate: React.FC<any> = (props) => {
  const [errorState, setErrState] = useState(undefined);
  const [error, setError] = useState<boolean>(false);
  const [channelError, setChannelError] = useState<boolean>(false);
  const { storeId } = props;
  const channelAccounts = useFlowChannelAccounts(
    props.channel,
    props.currentChannelId
  );
  const customerCustomFields = useAppSelector(
    (state) => state.customFieldsState.customerCustomFields
  );

  // Only date fields make sense for "journey starts relative to a date".
  const dateCustomFields = useMemo(
    () => customFieldHelper.getDateFieldsForRelativeTrigger(customerCustomFields),
    [customerCustomFields]
  );

  useEffect(() => {
    clearSharedQueryBuilderCache();
  }, []);

  const handleOnModalClose = () => {
    props.onClose();
  };

  const typesAPIHandler = useMemo(
    () => ({
      USER_PROPERTY_FILTER: async (p: BaseQueryBuilderPayload<undefined>) => {
        const response = await delayedUserPropertyResponse(p, storeId);
        return response;
      },
      HAS_CUSTOM_FIELD: async (
        userSelection: BaseQueryBuilderPayload<{
          propertyName?: string;
          searchKey?: string;
        }>
      ) => ({
        data: customFieldHelper.hasCustomFieldPropertyHandler(
          dateCustomFields,
          userSelection
        ),
      }),
    }),
    [storeId, dateCustomFields]
  );

  async function delayedUserPropertyResponse(p, storeId) {
    if (filterTimer) {
      clearTimeout(filterTimer);
    }
    return new Promise((resolve) => {
      filterTimer = setTimeout(async () => {
        const response = await userPropertyFilterAPIHandler(p, storeId, true);
        resolve(response);
      }, 800);
    });
  }

  const checkForErros = () => {
    const errorStateRes = segmentValidator.validate({
      include: builderState,
    });
    setErrState({ ...errorStateRes[1].include });
    setError(errorStateRes[0]);
    return { isError: errorStateRes[0], state: errorStateRes[1].include };
  };

  // The card header is the property the journey is relative to; the subheader
  // is the rest of the readable condition.
  const splitHeader = (node, subHeader: string) => {
    if (node.type === "HAS_CUSTOM_FIELD") {
      const header = customFieldHelper.getNameForId(
        node.properties?.[0]?.name,
        dateCustomFields
      );
      return { header, subHeader: removeSubstring(subHeader, header) };
    }
    if (subHeader.includes("Last Order Date")) {
      return {
        header: "Last Order Date",
        subHeader: removeSubstring(subHeader, "Last Order Date"),
      };
    }
    if (subHeader.includes("Initial Order Date")) {
      return {
        header: "First Order Date",
        subHeader: removeSubstring(subHeader, "Initial Order Date"),
      };
    }
    return {
      header: "Total Order Value",
      subHeader: removeSubstring(subHeader, "Total Order Value"),
    };
  };

  const parseBuilderData = () => {
    if (!channelAccounts.selected?.value) {
      setChannelError(true);
      return;
    }
    const { isError, state } = checkForErros();
    if (isError) return;

    const { frontendState, backendState } = sanitiseState(state);
    const { subHeader, id } = extractHeaderAndSubHeader(
      frontendState,
      dateCustomFields
    );
    const { header, subHeader: newSubHeader } = splitHeader(
      frontendState.nodes[0],
      subHeader
    );

    props.onSave({
      ...backendState,
      frontendNodes: frontendState.nodes,
      header,
      subHeader: newSubHeader,
      id,
      type: "relay",
      segmentId: props?.segmentId,
      channelId: channelAccounts.selected.value,
    });
    builderState = {};
    props.onClose();
  };

  return (
    <StyledModal
      onClose={handleOnModalClose}
      zIndex={200}
      open={props.visible}
      headerRightCustomElement={
        <ChannelAccountDropdown
          channel={props.channel}
          options={channelAccounts.options}
          selected={channelAccounts.selected}
          isLoading={channelAccounts.isLoading}
          select={(option) => {
            channelAccounts.select(option);
            setChannelError(false);
          }}
        />
      }
      headingTitle="Create trigger based on events"
      modalHeaderStyles={{ paddingBottom: 8 }}
      headerLeftCustomElement={
        <Tooltip
          width={280}
          body="The journey will trigger only for customers who enter the segment after the journey goes LIVE"
        >
          <div style={{ display: "flex", cursor: "pointer" }}>
            <Help size={24} color={COLORS.content.secondary} />
          </div>
        </Tooltip>
      }
      centralContainerStyles={{ width: "80%", minHeight: "80%" }}
      footerLeftCustomElement={
        (channelError || error) && (
          <Tag
            tagText={
              channelError
                ? "Please select an account"
                : "Some inputs are missing or incorrect. Please check."
            }
            type="negative"
            LeadingIcon={WarnIcon}
          />
        )
      }
      primaryButton={{
        buttonType: "primary",
        buttonText: "Create",
        onClick: () => parseBuilderData(),
      }}
    >
      {/* Figma keeps "Whenever customer" 12px under the title. */}
      <div style={{ padding: "0 24px 24px" }}>
        <QueryBuilder
          mode={props.mode}
          state={
            props.nodes && props.nodes.length
              ? { nodes: props.nodes }
              : undefined
          }
          addNodeBtnText="Add Filter"
          onStateChange={(state: any) => {
            builderState = state;
          }}
          queryPreText="Whenever customer"
          selectorKey="eventTrigger"
          propertyPlaceholders={PROPERTY_PLACEHOLDERS}
          typesAPIHandlers={typesAPIHandler}
          infinite={false}
          errorState={errorState}
        />
      </div>
    </StyledModal>
  );
};

export default SpecialDate;
